import React from 'react';
import { Trans } from '@gamiphy/react-i18n';
import { observer } from 'mobx-react';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { formatWelcomeGiftValue } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';
import { AppWelcomeGiftStore } from '@gamiphy/loyalty-station-desktop/store/appWelcomeGift';
import NotVerified from '@gamiphy/loyalty-station-desktop/components/cashback/cashbackProgram/verification/NotVerified';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { Box, Button } from '@mui/material';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import Congrats from './Congrats';

type Steps = 'unverified' | 'verified';

const WelcomeGift: React.FC = () => {
  const initialStep = React.useMemo<Steps>(() => {
    switch (true) {
      case !UserStore.user?.phone:
        return 'unverified';
      default:
        return 'verified';
    }
  }, [
    UserStore.user?.phone,
  ]);

  const [step, setStep] = React.useState<Steps>(initialStep);

  // Handle step change when no verification flow UI is present in welcome gift.
  React.useEffect(() => {
    if (UserStore.user?.phone && AppStore.isCashbackProgram) {
      setStep('verified');
    }
  }, [UserStore.user?.phone, AppStore.isCashbackProgram]);

  const notVerifiedDescription = React.useMemo(() => {
    if (AppWelcomeGiftStore.welcomeGift) {
      if (!AppStore.isCashbackProgram) {
        return (
          <Trans
            i18nKey="coreloyalty.cashback.welcomeGift.verifyPhone"
            defaults="To unlock a {{discountValue}} discount on your first order over {{minPurchase, currency}},  verify your phone number"
            values={{
              discountValue: formatWelcomeGiftValue(AppWelcomeGiftStore.welcomeGift),
              minPurchase: AppWelcomeGiftStore.welcomeGift?.minPurchaseAmount,
            }}
          />
        );
      }
      return (
        <Trans
          i18nKey="coreloyalty.cashback.welcomeGift.login"
          defaults="Login to unlock {{discountValue}} discount on your first order over {{minPurchase, currency}}!"
          values={{
            discountValue: formatWelcomeGiftValue(AppWelcomeGiftStore.welcomeGift),
            minPurchase: AppWelcomeGiftStore.welcomeGift?.minPurchaseAmount,
          }}
        />
      );
    }
    return null;
  }, [AppWelcomeGiftStore.welcomeGift, AppStore.isCashbackProgram]);

  const content = React.useMemo(() => {
    switch (step) {
      case 'unverified':
        return (
          AppStore.isCashbackProgram ? (
            <Box>
              {notVerifiedDescription}
              <Button
                variant="contained"
                fullWidth
                onClick={() => UiStore.openSignupPopup()}
                sx={{ textTransform: 'capitalize', mt: 1 }}
              >
                <Trans i18nKey="shared.login" defaults="Login" />
              </Button>
            </Box>
          ) : (
            <NotVerified
              onDone={() => setStep('verified')}
              description={notVerifiedDescription}
            />
          )
        );
      default:
        return (
          <Congrats />
        );
    }
  }, [step, notVerifiedDescription, AppStore.isCashbackProgram]);

  return content;
};

export default observer(WelcomeGift);
